import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import * as Dialog from '@radix-ui/react-dialog';
import { Theme, Button } from '@radix-ui/themes';
import '@radix-ui/themes/styles.css';
import './App.css';
import { FaInstagram, FaTwitter, FaFacebook, FaYoutube } from 'react-icons/fa';

// Centralized dialog configurations for easy maintenance
const dialogConfigs = {
  socials: {
    title: 'Social Media',
    description: 'Connect with us on social media platforms.',
    content: [
      { icon: FaInstagram, label: 'Instagram', href: 'https://www.instagram.com/augamatic/' },
      { icon: FaTwitter, label: 'Twitter', href: 'https://x.com/augustrigo' },
      { icon: FaFacebook, label: 'Facebook', href: 'https://www.facebook.com/people/August-Rigo/100058282382794/' },
      { icon: FaYoutube, label: 'YouTube', href: 'https://www.youtube.com/channel/UCpGVuTjlOrPu2MaPcJCGZqg' }
    ]
  },
  credits: {
    title: 'Credits',
    description: 'Music credits and acknowledgments.',
    content: [
      'BTS - ON',
      'BTS FT. SIA - ON',
      'BTS - BLACK SWAN',
      'CHRIS BROWN - BACK TO SLEEP',
      'CHRIS BROWN FT. USHER & ZAYN - BACK TO SLEEP (REMIX)',
      'CHRIS BROWN - TOUGH LOVE',
      'JUSTIN BIEBER - U SMILE',
      'JUSTIN BIEBER - STUCK IN THE MOMENT',
      'JUSTIN BIEBER - MARIA',
      'JUSTIN BIEBER - KISS AND TELL',
      'JIMIN FT. SOFIA CARSON - SLOW DANCE',
      'JIMIN - CLOSER THAN THIS',
      'ONE DIRECTION - GOTTA BE YOU',
      'SEVENTEEN - SUPER',
      'SEVENTEEN - HOT',
      'KEHLANI - KEEP ON',
      'IYAZ - SOLO',
      'TXT - BLUE SPRINGS',
      'TXT - GROWING PAINS',
      'MUSIQ SOULCHILD - ANYTHING',
      'MUSIQ SOULCHILD - SAYIDO',
      'MUSIQ SOULCHILD - SILVER&GOLD',
      'MUSIQ SOULCHILD - WAITINGSTILL',
      'MUSIQ SOULCHILD - CLUMSYLOVE',
      'SEAN KINGSTON - PARTY ALL NIGHT (SLEEP ALL DAY)',
      'BIG TIME RUSH - CONFETTI FALLING',
      'TRAIN - THE NEWS',
      'SWV - WHAT WE GONE DO',
      'DJ4B FT CHRIS BROWN - DID YOU',
      'BAEKHYUN - BETCHA',
      'NCT 127 - 1,2, 3',
      'NCT 127 - REAL LIFE',
      'NCT 127 - BACK 2 U [AM 01:27]',
      'NCT DREAM - ON & ON',
      'NCT DREAM - MY FIRST AND LAST',
      'JLS - OUTTA THIS WORLD',
      'JLS - WORK',
      'JLS - I KNOW WHAT SHE LIKE',
      'JLS - LOVE AT WAR',
      'JESSE MCCARTNEY - THE WRITER',
      'CHERYL COLE - BETTER TO LIE',
      'PLEASURE P - CHANGE POSITIONS',
      'KEY - G.O.A.T.',
      'KEY - BAD LOVE',
      'KEY - EIGHTEEN (END OF MY WORLD)',
      'RIIZE - 9 DAYS',
      'TVXQ! - CHAMPAGNE',
      '&TEAM - CRESCENT MOON\'S WISH',
      'DESTINY ROGERS - YELLOW NOTEPAD',
      'DESTINY ROGERS - BITTER',
      'DESTINY ROGERS - HOLY GHOST',
      'DESTINY ROGERS - LOCKDOWN',
      'BRIAN PUSPOS - CHERRY MANSION (ALBUM)',
      'BRIAN PUSPOS - SWEET 290 (EP)',
      'BRIAN PUSPOS - L.U.V. (EP)',
      'GIRLICIOUS - UNLEARN ME',
      'GIRLICIOUS - OVER YOU',
      'RILEYY LANEZ - ONE OF THEM NIGHTS',
      'TIA RAY - LUCKY RAIN',
      'NINETYSIX - SUPERCHARGER',
      'MINDLESS BEHAVIOR - FUTURE',
      'JESSICA JARRELL - UP & RUNNING',
      'SB19 - I WANT YOU'
    ]
  },
  bio: {
    title: 'Bio',
    description: 'Learn more about August Rigo.',
    content: [
      'Hailing from Toronto, Canada, August Rigo is a multifaceted Filipino artist and self-taught musician, renowned for his versatility. As a one-of-a-kind songwriter and producer, his talents encompass vocal production, composition, mixing, and engineering. After dedicating years working under the legendary producer duo Trackmasters, August achieved a breakthrough by writing for global icons such as Justin Bieber and One Direction. This success led to a publishing deal with SONY/ATV and a prolific period of collaboration with top-tier artists. His #1 hit \'BACK TO SLEEP\' with Chris Brown, is revered by critics as one of the best R&B songs of the decade.',
      'Despite having a record deal with Def Jam under LA Reid that did not result in an album, August remained steadfast in pursuing his own music. In 2015, he released his debut album, THE FALL OUT, through his own imprint, Summerchild Records. This project showcased his full range of skills and garnered critical acclaim, including a Juno nomination for Best R&B/Soul Recording.',
      'His artistic journey took a transformative turn as he ventured into the burgeoning world of K-pop. His passion for the genre led him to Seoul, where he became an early pioneer, working closely with emerging K-pop trainees and idols. This marked a second career-defining phase for August, highlighted by his work with international sensation BTS. He co-wrote the two leading singles, \'ON\' and \'BLACK SWAN,\' from their 7th studio album, MAP OF THE SOUL: 7, a groundbreaking project that debuted at number 1 on the Billboard 200 chart. Since then, his contributions to BTS, Jimin, Seventeen, and many others have solidified him as a key collaborator in the Korean music industry.',
      'With a career spanning over two decades and an unwavering commitment to perfecting his craft, August Rigo\'s legacy is defined by his dedication to musical excellence. His remarkable ability to seamlessly transition between roles as a songwriter, producer, and collaborator has kept him a gem in the industry. August\'s dynamic artistry continues to resonate with listeners worldwide, solidifying his status as a trailblazer in contemporary music and an influential force shaping its future.'
    ]
  },

};

function AppContent() {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [currentAnimation, setCurrentAnimation] = useState(null);
  const [currentButton, setCurrentButton] = useState(null);
  const [canvasSize, setCanvasSize] = useState({ width: 1280, height: 1280 });
  const [initialAnimationComplete, setInitialAnimationComplete] = useState(false);
  const [activeDialog, setActiveDialog] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(null);
  
  // Dedicated reference for the play animation
  const playAnimationRef = useRef(null);
  
  // Dedicated reference for the play down animation
  const playDownAnimationRef = useRef(null);

  // CDN base URL for all media assets
  const CDN = "https://arsite.sfo3.cdn.digitaloceanspaces.com";

  // August Rigo's 3 demo tracks hosted on DO Spaces
  const audioTracks = [
    { title: "What We Doin", url: `${CDN}/What%20We%20Doin%20%5BMP3%5D.mp3` },
    { title: "No Answer",    url: `${CDN}/No%20Answer%20%5BMP3%5D.mp3` },
    { title: "Next With You",url: `${CDN}/Next%20With%20You%20%5BMP3%5D.mp3` },
  ];

  // Audio refs and state
  const audioRef = useRef(null);
  const playTrackingTimerRef = useRef(null);
  const isPlayingRef = useRef(false); // ref to avoid stale closures
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false); // kills pulse after first tap

  // ── Play tracking via Firebase Firestore ────────────────────────────────────
  // Setup (one-time, takes ~5 minutes):
  // 1. Go to https://console.firebase.google.com → New project → name it "august-rigo"
  // 2. Click "Firestore Database" in the left sidebar → Create database → Start in production mode
  // 3. In Firestore rules, paste and publish:
  //      rules_version = '2';
  //      service cloud.firestore {
  //        match /databases/{database}/documents {
  //          match /plays/{doc} { allow create: if true; allow read: if false; }
  //        }
  //      }
  // 4. Go to Project Settings (gear icon) → General → scroll down to "Your apps" → Web app (</>)
  // 5. Copy the config object and paste the values below
  const FIREBASE_PROJECT_ID = "ar-site-plays";
  const FIREBASE_API_KEY    = "AIzaSyAqG1vVrzxZKX2e8ROMjeEdG3rAJpZjgCs";

  const trackPlay = async (trackTitle) => {
    if (!FIREBASE_PROJECT_ID || !FIREBASE_API_KEY) return;
    try {
      await fetch(
        `https://firestore.googleapis.com/v1/projects/${FIREBASE_PROJECT_ID}/databases/(default)/documents/plays?key=${FIREBASE_API_KEY}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fields: {
              track:     { stringValue: trackTitle },
              playedAt:  { timestampValue: new Date().toISOString() },
            },
          }),
        }
      );
    } catch (e) {
      // Silently ignore tracking errors — never interrupt the user experience
    }
  };
  // ────────────────────────────────────────────────────────────────────────────

  // Set initial audio src on mount
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.src = audioTracks[0].url;
    }
  }, []);

  // Audio event listeners — sync play state and trigger play tracking after 15s
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = () => {
      isPlayingRef.current = true;
      setIsPlaying(true);
      clearTimeout(playTrackingTimerRef.current);
      playTrackingTimerRef.current = setTimeout(() => {
        trackPlay(audioTracks[currentTrackIndex].title);
      }, 15000);
    };

    const handlePause = () => {
      isPlayingRef.current = false;
      setIsPlaying(false);
      clearTimeout(playTrackingTimerRef.current);
    };

    const handleEnded = () => {
      isPlayingRef.current = false;
      setIsPlaying(false);
      clearTimeout(playTrackingTimerRef.current);
      // Auto-advance to next track
      const next = (currentTrackIndex + 1) % audioTracks.length;
      setCurrentTrackIndex(next);
      audio.src = audioTracks[next].url;
      audio.load();
      audio.play();
    };

    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);
    return () => {
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [currentTrackIndex]);
  
  // Start the play animation loop
  const startPlayAnimation = () => {
    if (playAnimationRef.current) {
      // Animation already exists, just resume it
      playAnimationRef.current.resume();
    } else {
      // Create new play animation
      playAnimation('play', true);
    }
  };
  
  // Stop the play animation
  const stopPlayAnimation = () => {
    if (playAnimationRef.current) {
      playAnimationRef.current.kill();
      playAnimationRef.current = null;
    }
    // Also clear the current animation state if it's the play animation
    if (currentButton === 'play') {
      setCurrentAnimation(null);
      setCurrentButton(null);
    }
  };
  
  // Play the PlayDown animation once, then execute callback
  // onComplete: 'play' to start looping animation, 'pause' to stop and show static frame
  const playPlayDownAnimation = (onCompleteAction = 'play') => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Set a placeholder immediately to prevent race conditions with Spotify listener
    playDownAnimationRef.current = { pending: true };
    
    // If pausing, stop the play animation FIRST so it doesn't overwrite PlayDown frames
    if (onCompleteAction === 'pause') {
      stopPlayAnimation();
    }
    
    const context = canvas.getContext('2d');
    const config = animations['playDown'];
    const images = [];
    const frames = { frame: 0 };
    
    let loadedImages = 0;
    let failedImages = 0;
    
    for (let i = 0; i < config.frameCount; i++) {
      const img = new Image();

      img.onload = () => {
        loadedImages++;
        if (loadedImages + failedImages === config.frameCount) {
          if (loadedImages > 0) {
            startPlayDownAnimation();
          }
        }
      };
      
      img.onerror = () => {
        failedImages++;
        if (loadedImages + failedImages === config.frameCount) {
          if (loadedImages > 0) {
            startPlayDownAnimation();
          } else {
            // If PlayDown fails, execute the action directly
            if (onCompleteAction === 'play') {
              startPlayAnimation();
            } else {
              stopPlayAnimation();
            }
          }
        }
      };
      
      img.src = config.currentFrame(i);
      images.push(img);
    }
    
    function render() {
      context.clearRect(0, 0, canvas.width, canvas.height);
      const currentImg = images[frames.frame];
      
      if (currentImg && currentImg.complete && currentImg.naturalWidth > 0) {
        context.drawImage(currentImg, 0, 0, canvas.width, canvas.height);
      }
    }
    
    function startPlayDownAnimation() {
      canvas.width = canvasSize.width;
      canvas.height = canvasSize.height;
      
      const animation = gsap.to(frames, {
        frame: config.frameCount - 1,
        snap: "frame",
        ease: "none",
        duration: config.duration,
        onUpdate: render,
        onComplete: () => {
          playDownAnimationRef.current = null;
          // After PlayDown animation completes, execute the appropriate action
          if (onCompleteAction === 'play') {
            startPlayAnimation();
          } else {
            // Show the static TapeAnimationPlay/01.png image after pausing
            const staticImg = new Image();
            staticImg.onload = () => {
              context.clearRect(0, 0, canvas.width, canvas.height);
              context.drawImage(staticImg, 0, 0, canvas.width, canvas.height);
            };
            staticImg.src = `${CDN}/TapeAnimationPlay/01.webp`;
          }
        }
      });
      
      playDownAnimationRef.current = animation;
    }
  };

  // Animation configurations — all frames served from DO Spaces CDN
  const animations = {
    'tape': {
      frameCount: 75,
      currentFrame: (index) => `${CDN}/Tape%20Main%20anim/Tape%20Main%20anim_000${index < 9 ? '0' : ''}${(index + 1).toString()}.webp`,
      duration: 3
    },
    'bio': {
      frameCount: 11,
      currentFrame: (index) => `${CDN}/Bio_NoPlay/Bio_NoPlay_${(98 + index).toString().padStart(5, '0')}.webp`,
      duration: 0.8
    },
    'credits': {
      frameCount: 11,
      currentFrame: (index) => `${CDN}/Credits_NoPlay/Credits_NoPlay_${(87 + index).toString().padStart(5, '0')}.webp`,
      duration: 0.8
    },
    'home': {
      frameCount: 11,
      currentFrame: (index) => `${CDN}/Home_NoPlay/Home_NoPlay_${(76 + index).toString().padStart(5, '0')}.webp`,
      duration: 0.8
    },
    'next': {
      frameCount: 11,
      currentFrame: (index) => `${CDN}/Next_NoPlay/Next_NoPlay_${(120 + index).toString().padStart(5, '0')}.webp`,
      duration: 0.8
    },
    'prev': {
      frameCount: 11,
      currentFrame: (index) => `${CDN}/Prev_NoPlay/Prev_NoPlay_${(109 + index).toString().padStart(5, '0')}.webp`,
      duration: 0.8
    },
    'socials': {
      frameCount: 11,
      currentFrame: (index) => `${CDN}/Socials_NoPlay/Socials_NoPlay_${(131 + index).toString().padStart(5, '0')}.webp`,
      duration: 0.8
    },
    'play': {
      frameCount: 7,
      currentFrame: (index) => `${CDN}/TapeAnimationPlay/${(index + 1).toString().padStart(2, '0')}.webp`,
      duration: 0.7
    },
    'playDown': {
      frameCount: 12,
      currentFrame: (index) => `${CDN}/Play_Down/Play_Down_${(142 + index).toString().padStart(5, '0')}.webp`,
      duration: 0.5
    }
  };

  // Button configuration for easy editing
  const buttonConfig = [
    {
      id: 'home',
      title: 'Home'
    },
    {
      id: 'credits',
      title: 'Credits'
    },
    {
      id: 'bio',
      title: 'Bio'
    },
    {
      id: 'prev',
      title: 'Prev'
    },
    {
      id: 'play',
      title: 'Play'
    },
    {
      id: 'next',
      title: 'Next'
    },
    {
      id: 'socials',
      title: 'Socials'
    }
  ];

  // Function to calculate responsive canvas size
  const calculateCanvasSize = () => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;
    
    // Calculate the maximum size that fits within the container while maintaining 1:1 aspect ratio
    const maxSize = Math.min(containerWidth * 0.9, containerHeight * 0.85);
    
    setCanvasSize({
      width: 1280,
      height: 1280
    });
  };

  // Handle button clicks for dialogs
  const handleDialogButtonClick = (buttonId) => {
    if (buttonId === 'socials' || buttonId === 'credits' || buttonId === 'bio') {
      playAnimation(buttonId);
    }
  };

  // Clean up any existing play animation
  const cleanupPlayAnimation = () => {
    if (currentAnimation && currentButton === 'play') {
      try {
        // Just pause the animation instead of killing it completely
        currentAnimation.pause();
      } catch (error) {
        console.error('Error pausing animation:', error);
      }
      
      // Don't clear canvas - just pause the animation
      // Don't kill all GSAP tweens - too aggressive
      // Don't reset frames - preserve position
      
      setCurrentAnimation(null);
      setCurrentButton(null);
    }
  };

  // Handle play button click - toggles play/pause
  const handlePlayClick = () => {
    if (playDownAnimationRef.current) return;
    const audio = audioRef.current;
    if (!audio) return;

    setHasInteracted(true); // stop the pulse hint on first tap
    if (isPlayingRef.current) {
      // Pause: update state immediately so UI responds even before audio event
      isPlayingRef.current = false;
      setIsPlaying(false);
      playPlayDownAnimation('pause');
      audio.pause();
    } else {
      // If no src set yet, load the current track
      if (!audio.src || audio.src === window.location.href) {
        audio.src = audioTracks[currentTrackIndex].url;
        audio.load();
      }
      playPlayDownAnimation('play');
      audio.play().catch((err) => {
        // Play was blocked (e.g. browser policy) — roll back UI state
        console.warn('Audio play failed:', err);
        isPlayingRef.current = false;
        setIsPlaying(false);
        playDownAnimationRef.current = null;
        stopPlayAnimation();
      });
    }
  };

  // Handle next button click — switches track, plays if already playing
  const handleNextClick = () => {
    playAnimation('next');
    const nextIndex = (currentTrackIndex + 1) % audioTracks.length;
    setCurrentTrackIndex(nextIndex);
    const audio = audioRef.current;
    if (audio) {
      audio.src = audioTracks[nextIndex].url;
      audio.load();
      if (isPlayingRef.current) {
        audio.play().catch((err) => console.warn('Audio play failed:', err));
      }
    }
  };

  // Handle prev button click — switches track, plays if already playing
  const handlePrevClick = () => {
    playAnimation('prev');
    const prevIndex = currentTrackIndex === 0 ? audioTracks.length - 1 : currentTrackIndex - 1;
    setCurrentTrackIndex(prevIndex);
    const audio = audioRef.current;
    if (audio) {
      audio.src = audioTracks[prevIndex].url;
      audio.load();
      if (isPlayingRef.current) {
        audio.play().catch((err) => console.warn('Audio play failed:', err));
      }
    }
  };

  // Preload all button animation frames after tape animation completes
  // so every subsequent button click feels instant (frames already in browser cache)
  useEffect(() => {
    if (!initialAnimationComplete) return;

    const animationsToPreload = [
      'bio', 'credits', 'socials', 'home',
      'next', 'prev', 'play', 'playDown'
    ];

    // Small delay so preloading doesn't compete with the tape animation finishing
    const timer = setTimeout(() => {
      animationsToPreload.forEach((animId) => {
        const config = animations[animId];
        if (!config) return;
        for (let i = 0; i < config.frameCount; i++) {
          const img = new Image();
          img.src = config.currentFrame(i); // triggers fetch + cache, no tracking needed
        }
      });
    }, 800);

    return () => clearTimeout(timer);
  }, [initialAnimationComplete]); // eslint-disable-line

  // Handle home button click - replays the initial tape animation
  const handleHomeClick = () => {
    // Hide the overlay buttons during animation
    setInitialAnimationComplete(false);
    // Play the initial tape animation
    playAnimation('tape');
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      calculateCanvasSize();
    };

    window.addEventListener('resize', handleResize);
    calculateCanvasSize(); // Initial calculation
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const playAnimation = (animationId, loop = false) => {
    
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    
    const context = canvas.getContext('2d');
    
    // Stop current animation if playing, BUT don't stop the play animation
    if (currentAnimation && currentButton !== 'play') {
      currentAnimation.kill();
      setCurrentAnimation(null);
    }

    // Don't clear canvas immediately - keep current content until images are ready
    const config = animations[animationId];
    
    if (!config) {
      return;
    }
    
    const images = [];
    // Create a unique frames object for this animation
    const frames = { frame: 0 };
    
    // Store frames globally for cleanup access
    window.currentFrames = frames;
    
    // Preload images with better error handling and loading indicator
    let loadedImages = 0;
    let failedImages = 0;

    setLoadingProgress(0);

    for (let i = 0; i < config.frameCount; i++) {
      const img = new Image();

      img.onload = () => {
        loadedImages++;
        setLoadingProgress(Math.round(((loadedImages + failedImages) / config.frameCount) * 100));
        if (loadedImages + failedImages === config.frameCount) {
          setLoadingProgress(null);
          if (loadedImages > 0) {
            startAnimation(loop);
          } else {
            showErrorOnCanvas(context, 'No images could be loaded');
          }
        }
      };

      img.onerror = () => {
        failedImages++;
        setLoadingProgress(Math.round(((loadedImages + failedImages) / config.frameCount) * 100));
        if (loadedImages + failedImages === config.frameCount) {
          setLoadingProgress(null);
          if (loadedImages > 0) {
            startAnimation(loop);
          } else {
            showErrorOnCanvas(context, 'No images could be loaded');
          }
        }
      };
      
      // Try to load the image
      img.src = config.currentFrame(i);
      images.push(img);
    }

    function showErrorOnCanvas(context, message) {
      // Silently clear — dark background shows through, no jarring error UI
      context.clearRect(0, 0, canvas.width, canvas.height);
    }

    function render() {
      // Clear canvas on every frame to prevent layering and repeating effects
      context.clearRect(0, 0, canvas.width, canvas.height);
      
      const currentImg = images[frames.frame];
      
      if (currentImg && currentImg.complete && currentImg.naturalWidth > 0) {
        try {
          // Draw image to fill the entire canvas while maintaining aspect ratio
          context.drawImage(currentImg, 0, 0, canvas.width, canvas.height);
        } catch (error) {
          // Frame decode error — hold last good frame (do nothing)
        }
      } else {
        // Frame not ready yet — hold current canvas contents (do nothing)
      }
    }

    function startAnimation(loop) {
      // Set canvas size to the calculated responsive size
      canvas.width = canvasSize.width;
      canvas.height = canvasSize.height;
      
      // For 11-frame animations, play forward once
      if (config.frameCount === 11) {
        const animation = gsap.to(frames, {
          frame: config.frameCount - 1,
          snap: "frame",
          ease: "none",
          duration: config.duration,
          onUpdate: render,
                      onComplete: () => {
              setCurrentButton(null);
              setCurrentAnimation(null);
              // If this was a dialog animation, open the appropriate dialog
              if (animationId === 'socials' || animationId === 'credits' || animationId === 'bio') {
                setActiveDialog(animationId);
              }
            }
        });
        
        setCurrentAnimation(animation);
        setCurrentButton(animationId);
      } else if (config.frameCount === 7 && animationId === 'play' && loop) {
        // For TapeAnimationPlay animation when looping (playing music)
        const animation = gsap.to(frames, {
          frame: config.frameCount - 1,
          snap: "frame",
          ease: "power1.inOut", // Smooth easing instead of linear
          duration: config.duration,
          repeat: -1, // Infinite loop
          onUpdate: render,
          onComplete: () => {
            // Reset frame to 0 for the next loop
            frames.frame = 0;
          }
        });
        
        // Store the play animation reference
        if (animationId === 'play') {
          playAnimationRef.current = animation;
        }
        
        setCurrentAnimation(animation);
        setCurrentButton(animationId);
      } else {
        // Play once (for initial load, non-looping, or other animations)
        const animation = gsap.to(frames, {
          frame: config.frameCount - 1,
          snap: "frame",
          ease: "none",
          duration: config.duration,
          onUpdate: render,
          onComplete: () => {
            setCurrentButton(null);
            setCurrentAnimation(null);
            // If this is the tape animation, mark it as complete and show buttons
            if (animationId === 'tape') {
              setInitialAnimationComplete(true);
              
              // Show the static TapeAnimationPlay/01.png image after tape animation completes
              const canvas = canvasRef.current;
              if (canvas) {
                const context = canvas.getContext('2d');
                const img = new Image();
                img.onload = () => {
                  context.clearRect(0, 0, canvas.width, canvas.height);
                  context.drawImage(img, 0, 0, canvas.width, canvas.height);
                };
                img.src = `${CDN}/TapeAnimationPlay/01.webp`;
              }
            }
          }
        });

        setCurrentAnimation(animation);
        setCurrentButton(animationId);
      }
    }
  };


  useEffect(() => {
    // Initialize canvas with first frame of tape animation - only run once on mount
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const context = canvas.getContext('2d');
    
    // Set canvas size to the calculated responsive size
    canvas.width = canvasSize.width;
    canvas.height = canvasSize.height;

    // Start transparent — dark page background shows through until first frame arrives
    context.clearRect(0, 0, canvas.width, canvas.height);

    // Try to load the first frame and then autoplay
    const img = new Image();
    img.onload = () => {
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      // Auto-play the tape animation after a short delay
      setTimeout(() => {
        playAnimation("tape");
      }, 500);
    };
    img.onerror = () => {
      // Image failed to load, try to autoplay anyway
      setTimeout(() => {
        playAnimation("tape");
      }, 500);
    };
    img.src = `${CDN}/Tape%20Main%20anim/Tape%20Main%20anim_00001.webp`;
  }, []); // Remove canvasSize dependency to prevent replay on resize

  return (
    <Theme appearance="dark">
      <div className="App">
        {/* Hidden HTML5 Audio Player - src managed via ref to avoid conflicts */}
        <audio ref={audioRef} preload="metadata" />

        <div className="row">
          <div className="col">
            <div className="canvas-container" ref={containerRef}>
              <div className="canvas-wrapper">
              <canvas ref={canvasRef} />
              {loadingProgress !== null && (
                <div className="loading-bar-container">
                  <div className="loading-bar" style={{ width: `${loadingProgress}%` }} />
                </div>
              )}
              {initialAnimationComplete && (
                <div className="overlay-buttons">
                  {buttonConfig.map((button) => {
                    if (button.id === 'socials' || button.id === 'credits' || button.id === 'bio') {
                      return (
                        <button
                          key={button.id}
                          className={`overlay-btn overlay-btn-${button.id}`}
                          onClick={() => handleDialogButtonClick(button.id)}
                          title={button.title}
                        />
                      );
                    } else if (button.id === 'play') {
                      return (
                        <button
                          key={button.id}
                          className={`overlay-btn overlay-btn-${button.id} ${
                            currentButton === button.id ? "playing" : ""
                          } ${!hasInteracted ? "pulse-hint" : ""}`}
                          onClick={handlePlayClick}
                          title={isPlaying ? 'Pause' : 'Play'}
                        />
                      );
                    } else if (button.id === 'next') {
                      return (
                        <button
                          key={button.id}
                          className={`overlay-btn overlay-btn-${button.id} ${
                            currentButton === button.id ? "playing" : ""
                          }`}
                          onClick={handleNextClick}
                          title="Next Track"
                        />
                      );
                    } else if (button.id === 'prev') {
                      return (
                        <button
                          key={button.id}
                          className={`overlay-btn overlay-btn-${button.id} ${
                            currentButton === button.id ? "playing" : ""
                          }`}
                          onClick={handlePrevClick}
                          title="Previous Track"
                        />
                      );
                    } else if (button.id === 'home') {
                      return (
                        <button
                          key={button.id}
                          className={`overlay-btn overlay-btn-${button.id} ${
                            currentButton === button.id ? "playing" : ""
                          }`}
                          onClick={handleHomeClick}
                          title="Home - Replay Animation"
                        />
                      );
                    } else {
                      return (
                        <button
                          key={button.id}
                          className={`overlay-btn overlay-btn-${button.id} ${
                            currentButton === button.id ? "playing" : ""
                          }`}
                          onClick={() => playAnimation(button.id)}
                          title={button.title}
                        />
                      );
                    }
                  })}
                </div>
              )}
              </div>
            </div>
          </div>
        </div>

        {/* Socials Dialog - VHS Aesthetic */}
        <Dialog.Root open={activeDialog === 'socials'} onOpenChange={(open) => {
          if (!open) setActiveDialog(null);
        }}>
          <Dialog.Portal>
            <Dialog.Overlay className="dialog-overlay" />
            <Dialog.Content className="socials-dialog-content">
              {/* Corner Brackets */}
              <div className="corner-bracket top-left"></div>
              <div className="corner-bracket top-right"></div>
              <div className="corner-bracket bottom-left"></div>
              <div className="corner-bracket bottom-right"></div>
              

              
              {/* Vertical "Socials" text */}
              <div className="socials-vertical-text">Socials</div>
              
              <div className="socials-layout">
                {/* Left Side - Content */}
                <div className="socials-left">
                  <h2 className="socials-heading">CONNECT WITH AUGUST RIGO</h2>
                  
                  <div className="socials-grid">
                    {dialogConfigs.socials.content.map((item, index) => (
                      <a 
                        key={index} 
                        href={item.href} 
                        className="social-card"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <item.icon className="social-card-icon" />
                        <span className="social-card-label">{item.label.toUpperCase()}</span>
                      </a>
                    ))}
                  </div>
                  
                </div>
                
                {/* Right Side - Image */}
                <div className="socials-right">
                  <div className="socials-name-overlay">
                    <span className="name-large">AUGUST</span>
                    <span className="name-rigo">RIGO</span>
                  </div>
                </div>
              </div>
              
              
              {/* Close Button */}
              <Dialog.Close asChild>
                <button className="socials-close-btn" aria-label="Close">×</button>
              </Dialog.Close>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        {/* Credits Dialog */}
        <Dialog.Root open={activeDialog === 'credits'} onOpenChange={(open) => {
          if (!open) setActiveDialog(null);
        }}>
          <Dialog.Portal>
            <Dialog.Overlay className="dialog-overlay" />
            <Dialog.Content className="credits-dialog-content">
              <div className="corner-bracket top-left"></div>
              <div className="corner-bracket top-right"></div>
              <div className="corner-bracket bottom-left"></div>
              <div className="corner-bracket bottom-right"></div>

              <Dialog.Close asChild>
                <button className="socials-close-btn" aria-label="Close">×</button>
              </Dialog.Close>

              <img src={`${CDN}/credits.webp`} alt="Credits" className="credits-dialog-bg" />

              <div className="credits-dialog-overlay">
                <div className="credits-dialog-list">
                  {dialogConfigs.credits.content.map((item, index) => (
                    <div key={index} className="credits-dialog-item">{item}</div>
                  ))}
                </div>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

                 {/* Bio Dialog */}
         <Dialog.Root open={activeDialog === 'bio'} onOpenChange={(open) => {
           if (!open) setActiveDialog(null);
         }}>
           <Dialog.Portal>
             <Dialog.Overlay className="dialog-overlay" />
             <Dialog.Content className="bio-dialog-content">
               <Dialog.Close asChild>
                 <button className="socials-close-btn" aria-label="Close">×</button>
               </Dialog.Close>

               <img src={`${CDN}/bio.webp`} alt="August Rigo" className="bio-dialog-bg" />

               <div className="bio-dialog-overlay">
                 <div className="bio-dialog-text">
                   <p>August Rigo is a Filipino-Canadian songwriter, producer, and artist whose versatility has made him a sought-after force in music. A self-taught musician, he honed his craft under the legendary Trackmasters before breaking through with songs for global superstars including Justin Bieber and One Direction. His work earned him a publishing deal with Sony/ATV and led to a prolific run of collaborations, including Chris Brown's chart-topping hit "Back to Sleep," widely regarded as one of the decade's standout R&B records.</p>
                   <p>Following a signing with L.A. Reid at Def Jam Recordings, Rigo channeled his focus into his independent work, releasing his debut album <em>The Fall Out</em> in 2015 on his own imprint, Summerchild Records. The project was a showcase of his comprehensive talent and garnered critical acclaim, culminating in a JUNO Award nomination for Best R&B/Soul Recording.</p>
                   <p>Rigo's career took a transformative turn after venturing into the world of K-pop. He became an early pioneer of the genre, working closely with emerging trainees and idols. His contributions include co-writing the lead singles "On" and "Black Swan" for BTS's Billboard #1 album, <em>Map of the Soul: 7</em>, and penning for leading artists like Seventeen, Jimin, and NCT, cementing his role as a key collaborator in the genre.</p>
                   <p>With a career spanning over two decades, August Rigo is defined by his dedication to musical excellence. He continues to bridge genres and cultures — a trailblazer whose influence resonates worldwide.</p>
                 </div>
               </div>
             </Dialog.Content>
           </Dialog.Portal>
         </Dialog.Root>


      </div>
    </Theme>
  );
}

function App() {
  return <AppContent />;
}

export default App;
